import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@cargomatic/ui';
function getValueSummary(config, value) {
    if (value == null)
        return '\u2014';
    switch (config.kind) {
        case 'text':
            return value || '\u2014';
        case 'select-single':
        case 'autocomplete-single': {
            if (!value)
                return '\u2014';
            const opt = 'options' in config
                ? config.options.find((o) => o.value === value)
                : null;
            return (opt === null || opt === void 0 ? void 0 : opt.label) || String(value);
        }
        case 'select-multi':
        case 'autocomplete-multi': {
            // getChipValue may pre-resolve autocomplete-multi to a display string
            if (typeof value === 'string')
                return value || '\u2014';
            const arr = value;
            if (!arr.length)
                return '\u2014';
            if ('options' in config) {
                const labels = arr
                    .slice(0, 2)
                    .map((v) => { var _a; return ((_a = config.options.find((o) => o.value === v)) === null || _a === void 0 ? void 0 : _a.label) || v; });
                return arr.length > 2
                    ? `${labels.join(', ')} (+${arr.length - 2})`
                    : labels.join(', ');
            }
            return arr.length > 2
                ? `${arr.slice(0, 2).join(', ')} (+${arr.length - 2})`
                : arr.join(', ');
        }
        case 'date':
            return value instanceof Date ? value.toLocaleDateString() : '\u2014';
        case 'date-range': {
            const range = value;
            if (!(range === null || range === void 0 ? void 0 : range.from) || !(range === null || range === void 0 ? void 0 : range.to))
                return '\u2014';
            return `${range.from.toLocaleDateString()} \u2013 ${range.to.toLocaleDateString()}`;
        }
        case 'toggle':
            return value ? 'Yes' : 'No';
        default:
            return String(value);
    }
}
/**
 * Compact filter chip that shows Label: Value with ✕ to remove.
 * Click opens a popover with the full filter editor (passed as children).
 *
 * Usage:
 * ```
 * <FilterChip config={cfg} value={filterValues.status} onRemove={() => toggle('status')}>
 *   <MultiSelectEditor options={...} value={...} onChange={...} />
 * </FilterChip>
 * ```
 */
export function FilterChip({ config, value, onRemove, children, }) {
    const summary = getValueSummary(config, value);
    const hasValue = (() => {
        if (value == null || value === '')
            return false;
        if (config.kind === 'toggle')
            return true;
        if (value === false)
            return false;
        if (Array.isArray(value) && value.length === 0)
            return false;
        if (config.kind === 'date-range' &&
            (!value.from ||
                !value.to))
            return false;
        return true;
    })();
    const chipRef = useRef(null);
    const prevHasValue = useRef(hasValue);
    useEffect(() => {
        // Scroll into view when filter transitions from inactive → active
        if (hasValue && !prevHasValue.current && chipRef.current) {
            chipRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start',
            });
        }
        prevHasValue.current = hasValue;
    }, [hasValue]);
    return (React.createElement(Popover, { modal: false },
        React.createElement("div", { ref: chipRef, role: "group", "aria-label": `${config.label} filter`, className: `inline-flex items-center gap-1.5 h-8 px-2.5 text-xs font-medium rounded-md border transition-colors shrink-0 whitespace-nowrap
          ${hasValue ? 'bg-secondary border-secondary-foreground/20 text-secondary-foreground' : 'bg-background border-dashed text-muted-foreground hover:bg-accent'}` },
            React.createElement(PopoverTrigger, { asChild: true },
                React.createElement("button", { type: "button", "aria-label": hasValue
                        ? `${config.label}, ${summary} — press Enter to edit`
                        : `${config.label} filter — press Enter to set`, className: "inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-none p-0 text-inherit font-inherit text-xs rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1" },
                    React.createElement("span", { className: "font-semibold" }, config.label),
                    hasValue && (React.createElement(React.Fragment, null,
                        React.createElement("span", { className: "text-muted-foreground" }, ":"),
                        React.createElement("span", { className: "max-w-[150px] truncate" }, summary))))),
            React.createElement("button", { type: "button", "aria-label": `Remove ${config.label}`, onClick: onRemove, className: "ml-0.5 rounded-full p-0.5 hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" },
                React.createElement(X, { className: "size-3" }))),
        React.createElement(PopoverContent, { className: "w-80 p-3", align: "start", sideOffset: 8 }, children)));
}
